'use client';

import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';
import {
  PortalLink,
  useCurrentUser,
  useLocale,
  useBlockPart,
  useMarketplaceSource,
  type MarketplaceOrder,
} from '@akademi/site-builder-runtime';
import { Badge, Button, Card, CardContent, CardHeader, CardTitle, Checkbox } from '@akademi/ui';
import { CreditCard, Loader2 } from 'lucide-react';
import { SectionShell } from '../_shared/SectionShell';
import { resolvePaddingStyle, type PaddingBox } from '../_shared/resolvePadding';
import { tx } from '../_shared/coerce';
import { useMarketplaceCart } from './_useMarketplaceCart';
import {
  getMarketplaceRoot,
  isCartTotalZero,
  isCartEmpty,
  formatMarketPrice,
  formatOrderStatus,
  mpSurface,
  ORDER_APPROVED_STATUSES,
  GATEWAY_LABEL_KEYS,
} from './_shared';

type Localized = string | Record<string, string>;

export interface MarketplaceCheckoutCtaProps {
  termsLabel?: Localized;
  /** Preview-only fallback for whether to show "Register" instead of "Pay"
   *  — with a {@link useMarketplaceCart} source mounted, the live cart's
   *  `total` decides this instead (see `isFree` below). Ignored in
   *  `mode="order"`. */
  free?: boolean;
  loginHref?: string;
  /** `'cart'` (default): the live `/marketplace/cart` terms+pay footer.
   *  `'order'`: one placed order's status/total + a "pay with…" button per
   *  gateway it still allows (or "start learning" once approved). Pairs
   *  with {@link MarketplaceCartItems}'s own `mode="order"` for
   *  `/marketplace/orders/[id]`. */
  mode?: 'cart' | 'order';
  /** Required when `mode="order"` — bound from the route's `[id]` segment
   *  by `bindPathParams.ts`. Internal only: not exposed in the block's
   *  schema, so operators can't set it from the admin UI. */
  orderId?: string;
  /** `mode="order"` only — also lists the order's line-item titles above
   *  the total, so this card can stand on its own (e.g. next to
   *  {@link MarketplaceBankTransferInstructions}) without a separate
   *  {@link MarketplaceCartItems} block. */
  showCartDetail?: boolean;
  /** `mode="order"` only — the "start learning" / pay-with-gateway
   *  button(s). Default on for the order-detail page; turn off on a
   *  gateway's own instructions page (e.g. bank-transfer), where a
   *  redirect back into the gateway you're already on — or a switch to a
   *  different one mid-flow — doesn't make sense. Matches the source
   *  app's `PaymentResumeInfoCard`, which never renders these there. */
  showActions?: boolean;
  paddingOuter?: PaddingBox;
  paddingInner?: PaddingBox;
}

/**
 * Terms checkbox + primary checkout action — akademi-lms-v2's cart page
 * footer CTA. Reads {@link useCurrentUser} to switch between "log in" and
 * the pay action. With a {@link useMarketplaceSource} mounted, "Make
 * payment" requests the real gateway redirect URL
 * (`POST /marketplace/payment/{gateway}/create-payment-gateway-url`) and
 * navigates to it; with no source the click is an inert preview — the
 * "fallbacks stay, for testing" contract.
 */
export function MarketplaceCheckoutCta({
  termsLabel,
  free = false,
  loginHref = '/sign-in?returnUrl=marketplace/cart',
  mode = 'cart',
  orderId,
  showCartDetail = false,
  showActions = true,
  paddingOuter,
  paddingInner,
}: MarketplaceCheckoutCtaProps) {
  const { t } = useTranslation('site-builder-blocks');
  const { locale } = useLocale();
  const part = useBlockPart();
  const user = useCurrentUser();
  const source = useMarketplaceSource();
  const cart = useMarketplaceCart();
  const [agreed, setAgreed] = useState(false);
  const [busy, setBusy] = useState(false);
  const isOrderMode = mode === 'order';

  const [order, setOrder] = useState<MarketplaceOrder | null>(null);
  const [orderLoadStatus, setOrderLoadStatus] = useState<'loading' | 'ready' | 'error'>(
    isOrderMode && source && orderId ? 'loading' : 'ready',
  );
  const [payingGateway, setPayingGateway] = useState<string | null>(null);

  useEffect(() => {
    if (!isOrderMode || !source || !orderId) return;
    let cancelled = false;
    setOrderLoadStatus('loading');
    source
      .getOrder(orderId)
      .then((fetched) => {
        if (cancelled) return;
        setOrder(fetched);
        setOrderLoadStatus('ready');
      })
      .catch(() => {
        if (!cancelled) setOrderLoadStatus('error');
      });
    return () => {
      cancelled = true;
    };
  }, [isOrderMode, source, orderId]);

  async function handlePayOrder(gateway: string) {
    if (!source || !orderId) return;
    if (gateway === 'bank_transfer') {
      window.location.href = `${getMarketplaceRoot()}/payment/bank_transfer/${orderId}`;
      return;
    }
    setPayingGateway(gateway);
    try {
      const { url, purchaseOrderId: resumedId } = await source.getPaymentGatewayRedirectUrl(
        gateway,
        orderId,
      );
      if (url) {
        window.location.href = url;
        return;
      }
      if (resumedId) {
        window.location.href = `${getMarketplaceRoot()}/payment/${gateway}/${resumedId}`;
        return;
      }
    } finally {
      setPayingGateway(null);
    }
  }

  if (isOrderMode) {
    const totalText = order?.total ? formatMarketPrice(order.total, locale) : undefined;
    const isApproved = Boolean(order?.status && ORDER_APPROVED_STATUSES.has(order.status));
    const isLoading = Boolean(source && orderId) && orderLoadStatus === 'loading';

    return (
      <SectionShell paddingOuter={paddingOuter}>
        <Card style={{ ...mpSurface, ...resolvePaddingStyle(paddingInner) }}>
          <CardHeader className="p-0">
            <CardTitle>
              {t('marketplace.orderDetail.summary', { defaultValue: 'Summary' })}
            </CardTitle>
          </CardHeader>
          <CardContent className="px-0 pb-0 pt-3">
            {isLoading ? (
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'center',
                  padding: 'var(--space-8, 32px)',
                }}
              >
                <Loader2 size={24} className="animate-spin" />
              </div>
            ) : (
              <div
                style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-3, 12px)' }}
              >
                {order?.status && (
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      paddingBottom: 'var(--space-3, 12px)',
                      borderBottom: '1px solid var(--color-border-subtle, rgba(0,0,0,0.08))',
                    }}
                  >
                    <span
                      style={{
                        fontSize: 'var(--font-size-xs, 12px)',
                        color: 'var(--color-text-tertiary, #71717a)',
                      }}
                    >
                      {t('marketplace.bankTransfer.status', { defaultValue: 'Status' })}
                    </span>
                    <Badge variant={isApproved ? 'default' : 'secondary'}>
                      {formatOrderStatus(order.status, t)}
                    </Badge>
                  </div>
                )}

                {showCartDetail && order?.items && order.items.length > 0 && (
                  <ul {...part('list')} style={{ listStyle: 'none', padding: 0, margin: 0 }}>
                    {order.items.map((item, i) => (
                      <li
                        key={i}
                        style={{
                          fontSize: 'var(--font-size-sm, 13px)',
                          padding: '4px 0',
                        }}
                      >
                        {item.title}
                      </li>
                    ))}
                  </ul>
                )}

                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    ...(showCartDetail && order?.items && order.items.length > 0
                      ? {
                          paddingTop: 'var(--space-3, 12px)',
                          borderTop: '1px solid var(--color-border-subtle, rgba(0,0,0,0.08))',
                        }
                      : {}),
                  }}
                >
                  <span style={{ fontWeight: 600 }}>
                    {t('marketplace.bankTransfer.totalDue', { defaultValue: 'Total due' })}
                  </span>
                  <span style={{ fontWeight: 700, fontSize: 'var(--font-size-lg, 18px)' }}>
                    {totalText ?? '—'}
                  </span>
                </div>

                {showActions &&
                  (isApproved ? (
                    <Button asChild variant="brand" style={{ width: '100%' }}>
                      <PortalLink href="/courses">
                        {t('marketplace.orderDetail.startCourses', {
                          defaultValue: 'Start learning',
                        })}
                      </PortalLink>
                    </Button>
                  ) : (
                    <div
                      {...part('payment-actions')}
                      style={{
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 'var(--space-2, 8px)',
                      }}
                    >
                      {(order?.enabledPaymentGateways ?? []).map((gateway) => (
                        <Button
                          key={gateway}
                          type="button"
                          variant="brand"
                          disabled={payingGateway === gateway}
                          onClick={() => handlePayOrder(gateway)}
                          style={{ width: '100%' }}
                        >
                          {payingGateway === gateway ? (
                            <Loader2 size={16} className="animate-spin" />
                          ) : (
                            t(
                              GATEWAY_LABEL_KEYS[gateway] ?? 'marketplace.paymentMethod.creditCard',
                              { defaultValue: gateway },
                            )
                          )}
                        </Button>
                      ))}
                      {(!order?.enabledPaymentGateways ||
                        order.enabledPaymentGateways.length === 0) && (
                        <p
                          style={{
                            color: 'var(--color-text-tertiary, #71717a)',
                            fontSize: 'var(--font-size-sm, 13px)',
                          }}
                        >
                          {t('marketplace.orderDetail.noGateways', {
                            defaultValue: 'No payment method is available for this order yet.',
                          })}
                        </p>
                      )}
                    </div>
                  ))}
              </div>
            )}
          </CardContent>
        </Card>
      </SectionShell>
    );
  }

  if (cart && isCartEmpty(cart.cart)) return null;

  // With a source mounted, the live cart total is the single truth — a
  // 0-amount cart (fully covered by a coupon, or an all-free selection)
  // must show "Register" even if nobody remembered to flip the authored
  // `free` toggle for this particular order. Only fall back to the
  // authored prop with no source to reflect (matches CartSummary's
  // `isEmpty` posture).
  const isFree = cart ? isCartTotalZero(cart.cart) : free;

  // The gateway is normally the one picked in `MarketplacePaymentMethod`
  // (shared via `useMarketplaceCart`) — the button below stays disabled
  // until `cart.selectedGateway` is set, so this never fires without one.
  // A fully free cart has no gateway to pick (there's nothing to pay for),
  // so it falls back to the "registration" pseudo-gateway instead of
  // blocking on a selection that will never happen.
  const handlePay = async () => {
    const gateway = cart?.selectedGateway ?? (isFree ? 'registration' : undefined);
    if (!source || !gateway) return;
    setBusy(true);
    try {
      const { url, purchaseOrderId } = await source.getPaymentGatewayRedirectUrl(gateway);
      if (url) {
        window.location.href = url;
        return;
      }
      if (purchaseOrderId) {
        const marketplaceRoot = getMarketplaceRoot();
        window.location.href =
          gateway === 'registration'
            ? `${marketplaceRoot}/orders/${purchaseOrderId}`
            : `${marketplaceRoot}/payment/${gateway}/${purchaseOrderId}`;
        return;
      }
      throw new Error('no redirect target');
    } catch {
      toast.error(t('marketplace.coupon.invalid', { defaultValue: 'Something went wrong.' }));
      setBusy(false);
    }
  };

  return (
    <SectionShell paddingOuter={paddingOuter} paddingInner={paddingInner}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-3, 12px)' }}>
        {Boolean(user) && (
          <label
            style={{
              display: 'flex',
              alignItems: 'flex-start',
              gap: 'var(--space-2, 8px)',
              cursor: 'pointer',
            }}
          >
            <Checkbox checked={agreed} onCheckedChange={(v) => setAgreed(v === true)} />
            <span style={{ fontSize: 'var(--font-size-sm, 13px)' }}>
              {tx(termsLabel) || t('marketplace.checkoutCta.termsLabel')}
            </span>
          </label>
        )}
        {user ? (
          <Button
            type="button"
            variant="brand"
            disabled={
              !agreed || busy || Boolean(source && cart && !isFree && !cart.selectedGateway)
            }
            style={{
              width: '100%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 8,
            }}
            onClick={source ? handlePay : undefined}
          >
            {!isFree && <CreditCard size={16} aria-hidden />}
            {isFree
              ? t('marketplace.checkoutCta.registerFree')
              : t('marketplace.checkoutCta.makePayment')}
          </Button>
        ) : (
          <Button asChild variant="brand" style={{ width: '100%' }}>
            <PortalLink href={loginHref}>{t('marketplace.checkoutCta.login')}</PortalLink>
          </Button>
        )}
      </div>
    </SectionShell>
  );
}
